import { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

const HANDLE_SIZE = 20;
const HANDLE_RADIUS = HANDLE_SIZE / 2;

const SliderContainer = styled.div`
	width: 100%;
	padding: 2.5rem 0 0.5rem;
	position: relative;
`;

const TrackWrapper = styled.div`
	width: 100%;
	padding: 0 ${HANDLE_RADIUS}px;
	box-sizing: border-box;
`;

const Track = styled.div`
	position: relative;
	width: 100%;
	height: 8px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 4px;
	cursor: pointer;
`;

const Range = styled.div`
	position: absolute;
	height: 100%;
	border-radius: 4px;
`;

const InactiveRange = styled(Range)`
	background: rgba(255, 255, 255, 0.1);
`;

const ActiveRange = styled(Range)`
	background: ${({ theme }) => theme.colors.green};
`;

const CurrentTimeIndicator = styled.div`
	position: absolute;
	top: -2px;
	width: 2px;
	height: 12px;
	background: #ffffff;
	border-radius: 1px;
	transform: translateX(-50%);
	z-index: 5;
	pointer-events: none;
`;

const Handle = styled.div`
	position: absolute;
	top: 50%;
	width: ${HANDLE_SIZE}px;
	height: ${HANDLE_SIZE}px;
	background: ${({ $active, theme }) => ($active ? theme.colors.green : '#ffffff')};
	border: 2px solid ${({ theme }) => theme.colors.green};
	border-radius: 50%;
	transform: translate(-50%, -50%);
	cursor: grab;
	z-index: 10;
	transition: all 0.2s ease;

	&:hover {
		transform: translate(-50%, -50%) scale(1.2);
	}

	&:active {
		cursor: grabbing;
		transform: translate(-50%, -50%) scale(1.1);
	}
`;

const HandleLabel = styled.div`
	position: absolute;
	top: -32px;
	left: 50%;
	transform: translateX(-50%);
	font-size: 0.75rem;
	color: rgba(255, 255, 255, 0.8);
	white-space: nowrap;
	background: rgba(0, 0, 0, 0.7);
	padding: 0.25rem 0.5rem;
	border-radius: 0.25rem;
	pointer-events: none;
`;

const RangeSlider = ({ min, max, startValue, endValue, currentTime, onStartChange, onEndChange, onDragStart }) => {
	const trackRef = useRef(null);
	const [dragging, setDragging] = useState(null);

	const MIN_GAP = Math.min(1, Math.max(0, max - min));
	const clamp = (value) => Math.max(min, Math.min(max, value));

	const getPositionFromValue = (value) => {
		if (max === min) return 0;
		const position = ((value - min) / (max - min)) * 100;
		// Clamp to 0-100 to prevent floating point errors
		return Math.max(0, Math.min(100, position));
	};

	const getValueFromPosition = (clientX) => {
		const track = trackRef.current;
		if (!track) return min;

		const rect = track.getBoundingClientRect();
		if (rect.width <= 0) return min;
		const position = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		return min + position * (max - min);
	};

	const handleMouseDown = (handle) => (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragging(handle);
		onDragStart?.(handle);
	};

	const handleMouseMove = (e) => {
		if (!dragging) return;

		const newValue = getValueFromPosition(e.clientX);

		if (dragging === 'start') {
			onStartChange(clamp(Math.min(newValue, endValue - MIN_GAP)));
		} else if (dragging === 'end') {
			onEndChange(clamp(Math.max(newValue, startValue + MIN_GAP)));
		}
	};

	const handleMouseUp = () => {
		setDragging(null);
	};

	const handleTrackClick = (e) => {
		if (dragging) return;

		const newValue = getValueFromPosition(e.clientX);
		const startPos = getPositionFromValue(startValue);
		const endPos = getPositionFromValue(endValue);
		const clickPos = getPositionFromValue(newValue);

		const distToStart = Math.abs(clickPos - startPos);
		const distToEnd = Math.abs(clickPos - endPos);

		if (distToStart < distToEnd) {
			onStartChange(clamp(Math.min(newValue, endValue - MIN_GAP)));
		} else {
			onEndChange(clamp(Math.max(newValue, startValue + MIN_GAP)));
		}
	};

	useEffect(() => {
		if (dragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);

			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [dragging, startValue, endValue]);

	const startPos = getPositionFromValue(startValue);
	const endPos = getPositionFromValue(endValue);
	const currentPos = getPositionFromValue(currentTime);

	return (
		<SliderContainer>
			<TrackWrapper>
				<Track ref={trackRef} onClick={handleTrackClick}>
					<InactiveRange style={{ left: 0, width: `${startPos}%` }} />
					<InactiveRange style={{ left: `${endPos}%`, width: `${100 - endPos}%` }} />

					<ActiveRange style={{ left: `${startPos}%`, width: `${endPos - startPos}%` }} />

					{currentTime >= min && currentTime <= max && (
						<CurrentTimeIndicator style={{ left: `${currentPos}%` }} />
					)}

					<Handle
						style={{ left: `${startPos}%` }}
						onMouseDown={handleMouseDown('start')}
						$active={dragging === 'start'}>
						<HandleLabel>Start</HandleLabel>
					</Handle>

					<Handle
						style={{ left: `${endPos}%` }}
						onMouseDown={handleMouseDown('end')}
						$active={dragging === 'end'}>
						<HandleLabel>End</HandleLabel>
					</Handle>
				</Track>
			</TrackWrapper>
		</SliderContainer>
	);
};

export default RangeSlider;
